import React, { useRef, useEffect, useState } from "react";
import { Bold, Italic, Heading, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight, AlignJustify, Loader2 } from "lucide-react";

const exec = (cmd, val = null) => document.execCommand(cmd, false, val);
const escAlt = (s) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

// Editor visuale leggero (contentEditable) con toolbar: grassetto/corsivo/titolo,
// elenchi, link e inserimento immagini con allineamento (sinistra/centro/destra)
// e testo che avvolge la foto (classi cp-img-*).
export const RichTextEditor = ({ value, onChange, uploadImage, placeholder }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const alignRef = useRef("center");
  const [uploading, setUploading] = useState(false);
  const [imgSel, setImgSel] = useState(null);
  const [altDraft, setAltDraft] = useState("");

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (c, v) => { ref.current.focus(); exec(c, v); emit(); };
  const heading = () => { ref.current.focus(); exec("formatBlock", "H2"); emit(); };
  const link = () => {
    const url = window.prompt("Inserisci l'URL del link (es. https://…):");
    if (url) { ref.current.focus(); exec("createLink", url); emit(); }
  };
  const pickImage = (align) => { alignRef.current = align; if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); } };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await uploadImage(f);
      const alt = (window.prompt("Testo alternativo (alt): descrivi cosa mostra l'immagine", "") || "").trim();
      ref.current.focus();
      exec("insertHTML", `<img src="${url}" class="cp-img-${alignRef.current}" alt="${escAlt(alt)}" />&#8203;`);
      emit();
    } catch (_) { /* errore già notificato dal chiamante */ }
    finally { setUploading(false); }
  };

  const onEditorClick = (e) => {
    if (e.target.tagName === "IMG") { setImgSel(e.target); setAltDraft(e.target.getAttribute("alt") || ""); }
  };
  const applyAlt = () => {
    if (!imgSel) return;
    imgSel.setAttribute("alt", altDraft.trim());
    setImgSel(null);
    emit();
  };

  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" data-testid={testid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="w-8 h-8 rounded-md flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-[#16345c] transition-colors">
      {children}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <Btn onClick={() => cmd("bold")} title="Grassetto" testid="rte-bold"><Bold size={16} /></Btn>
        <Btn onClick={() => cmd("italic")} title="Corsivo" testid="rte-italic"><Italic size={16} /></Btn>
        <Btn onClick={heading} title="Titolo" testid="rte-heading"><Heading size={16} /></Btn>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <Btn onClick={() => cmd("insertUnorderedList")} title="Elenco puntato" testid="rte-ul"><List size={16} /></Btn>
        <Btn onClick={() => cmd("insertOrderedList")} title="Elenco numerato" testid="rte-ol"><ListOrdered size={16} /></Btn>
        <Btn onClick={() => cmd("justifyFull")} title="Giustifica" testid="rte-justify"><AlignJustify size={16} /></Btn>
        <Btn onClick={link} title="Link" testid="rte-link"><Link2 size={16} /></Btn>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <span className="text-[11px] font-semibold text-gray-400 px-1 select-none">Immagine:</span>
        <Btn onClick={() => pickImage("left")} title="Immagine a sinistra (il testo la avvolge a destra)" testid="rte-img-left"><AlignLeft size={16} /></Btn>
        <Btn onClick={() => pickImage("center")} title="Immagine centrata" testid="rte-img-center"><AlignCenter size={16} /></Btn>
        <Btn onClick={() => pickImage("right")} title="Immagine a destra (il testo la avvolge a sinistra)" testid="rte-img-right"><AlignRight size={16} /></Btn>
        {uploading && <Loader2 size={15} className="animate-spin text-[#ff7e00] ml-1" />}
        <span className="text-[10.5px] text-gray-400 ml-auto pr-1 select-none">Clicca un'immagine per modificarne l'alt</span>
      </div>
      {imgSel && (
        <div className="flex items-center gap-2 border-b border-gray-200 bg-amber-50 px-3 py-2" data-testid="rte-alt-bar">
          <span className="text-[11.5px] font-semibold text-gray-600 shrink-0">Testo alternativo (alt)</span>
          <input value={altDraft} onChange={(e) => setAltDraft(e.target.value)} data-testid="rte-alt-input" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyAlt(); } }}
            placeholder="Descrivi cosa mostra l'immagine" className="flex-1 min-w-0 border border-gray-200 rounded-md px-2 py-1 text-[13px] outline-none focus:border-[#16345c]" />
          <button type="button" onClick={applyAlt} data-testid="rte-alt-apply" className="text-[12px] font-bold text-white bg-[#16345c] rounded-md px-2.5 py-1">Applica</button>
          <button type="button" onClick={() => setImgSel(null)} data-testid="rte-alt-cancel" className="text-[12px] font-semibold text-gray-500">Annulla</button>
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onClick={onEditorClick}
        data-placeholder={placeholder || "Scrivi il contenuto della pagina…"}
        data-testid="rte-content"
        className="rte-content px-3 py-3 max-h-[420px] overflow-y-auto bg-white"
      />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={onFile} className="hidden" data-testid="rte-image-input" />
    </div>
  );
};

export default RichTextEditor;
